import RouteConstant from "navigation/constant";

const LINK_CODE_STORAGE_KEY = "talkio_linkCode";
const LINK_CODE_WIDGET_POS_KEY = "talkio_linkCode_widget_pos";
const LINK_CODE_WIDGET_INTRO_SHOWN_KEY = "talkio_linkCode_widget_intro_shown";

export const LINK_CODE_UPDATED_EVENT = "talkio-link-code-updated";
export const LINK_CODE_CLEARED_EVENT = "talkio-link-code-cleared";

/** Flows that send X-Link-Code via HTTP (defined manually per API module). */
export type LinkCodeFlowType = "phone-booking" | "recharge";

interface StoredLinkCode {
  linkCode: string;
}

export interface LinkCodeWidgetPosition {
  x: number;
  y: number;
}

export interface LinkCodeGuideConfig {
  title: string;
  message: string;
  actionLabel: string;
  redirectPath: string;
}

export type LinkCodeWidgetButtonIcon = "phone" | "recharge" | "login";

export interface LinkCodeWidgetButton {
  id: string;
  label: string;
  redirectPath: string;
  icon: LinkCodeWidgetButtonIcon;
  /** When true, logged-out users are sent to sign-in with redirect-url. */
  requiresLogin?: boolean;
  /** Shown only when the user is logged out. */
  guestOnly?: boolean;
}

/**
 * Add new referral actions here — the widget renders one button per entry.
 * Use a unique `id`, pick an `icon` key, and set `redirectPath`.
 */
export const LINK_CODE_WIDGET_BUTTONS: LinkCodeWidgetButton[] = [
  {
    id: "phone-booking",
    label: "Book your number",
    redirectPath: RouteConstant.SELECT_SERVICE,
    icon: "phone",
  },
  {
    id: "recharge",
    label: "Recharge",
    redirectPath: RouteConstant.RECHARGE_PLANS,
    icon: "recharge",
    requiresLogin: true,
  },
  // {
  //   id: "sign-in",
  //   label: "Sign in",
  //   redirectPath: RouteConstant.LOGIN,
  //   icon: "login",
  //   guestOnly: true,
  // },
];

export const getVisibleLinkCodeWidgetButtons = (
  isLoggedIn: boolean,
): LinkCodeWidgetButton[] =>
  LINK_CODE_WIDGET_BUTTONS.filter((button) => !button.guestOnly || !isLoggedIn);

/** Logged-out users go to sign-in only when the button requires login. */
export const getLinkCodeButtonNavigatePath = (
  button: LinkCodeWidgetButton,
  isLoggedIn: boolean,
): string => {
  if (isLoggedIn || button.guestOnly || !button.requiresLogin) {
    return button.redirectPath;
  }

  const params = new URLSearchParams();
  params.set("redirect-url", button.redirectPath);
  return `${RouteConstant.LOGIN}?${params.toString()}`;
};

const VIEWPORT_EDGE_MARGIN = 16;
const VIEWPORT_BOTTOM_MARGIN = 32;

export const getViewportMetrics = () => {
  const viewport = window.visualViewport;

  return {
    width: viewport?.width ?? window.innerWidth,
    height: viewport?.height ?? window.innerHeight,
    offsetTop: viewport?.offsetTop ?? 0,
    offsetLeft: viewport?.offsetLeft ?? 0,
  };
};

export const getLinkCodeWidgetWidth = (): number => {
  const { width } = getViewportMetrics();
  return Math.min(LINK_CODE_WIDGET_WIDTH, width - VIEWPORT_EDGE_MARGIN * 2);
};

export const getLinkCodeWidgetExpandedHeight = (buttonCount: number): number => {
  const headerHeight = 52;
  const bodyBaseHeight = 190;
  const buttonHeight = 60;
  const estimatedHeight =
    headerHeight + bodyBaseHeight + buttonCount * buttonHeight;

  const { height } = getViewportMetrics();
  const maxAllowedHeight = height - VIEWPORT_EDGE_MARGIN - VIEWPORT_BOTTOM_MARGIN;

  return Math.min(estimatedHeight, maxAllowedHeight);
};

export interface LinkCodeWidgetSize {
  width: number;
  height: number;
}

export const LINK_CODE_WIDGET_WIDTH = 320;
export const LINK_CODE_WIDGET_EXPANDED_HEIGHT = getLinkCodeWidgetExpandedHeight(
  LINK_CODE_WIDGET_BUTTONS.length,
);
export const LINK_CODE_WIDGET_MINIMIZED_SIZE = 52;

const PHONE_BOOKING_PATHS: string[] = [
  RouteConstant.SELECT_SERVICE,
  RouteConstant.CONFIRM_NUMBER,
  RouteConstant.CREATE_ACCOUNT,
  RouteConstant.CHECKOUT,
  RouteConstant.ORDER_STATUS,
];

const RECHARGE_PATHS: string[] = [
  RouteConstant.RECHARGE_PLANS,
  RouteConstant.PRODUCT_BUNDLE_PLANS,
  RouteConstant.MY_WALLET,
  RouteConstant.AIRTIME_BALANCE,
];

const dispatchLinkCodeEvent = (eventName: string): void => {
  window.dispatchEvent(new CustomEvent(eventName));
};

/** Resolve guide flow from the current route (linkType is not read from URL). */
export const getLinkTypeForPathname = (
  pathname: string,
): LinkCodeFlowType | undefined => {
  if (PHONE_BOOKING_PATHS.includes(pathname)) return "phone-booking";
  if (RECHARGE_PATHS.includes(pathname)) return "recharge";
  return undefined;
};

export const getLinkCodeGuideConfig = (
  linkType?: LinkCodeFlowType,
): LinkCodeGuideConfig => {
  switch (linkType) {
    case "phone-booking":
      return {
        title: "Referral link active",
        message:
          "Continue to book your Talkio number. Your referral benefit will be applied when you complete the booking.",
        actionLabel: "Book your number",
        redirectPath: RouteConstant.SELECT_SERVICE,
      };
    case "recharge":
      return {
        title: "Referral link active",
        message:
          "Continue to recharge airtime or bundles. Your referral benefit will be applied during payment.",
        actionLabel: "Go to recharge",
        redirectPath: RouteConstant.RECHARGE_PLANS,
      };
    default:
      return {
        title: "Referral link active",
        message:
          "Continue to get started. Your referral benefit will be applied when you complete your purchase.",
        actionLabel: "Get started",
        redirectPath: RouteConstant.GET_STARTED,
      };
  }
};

const getLinkCodeIntroSeenKey = (linkCode: string): string =>
  `talkio_linkCode_intro_seen:${linkCode}`;

export interface LinkCodeCaptureResult {
  captured: boolean;
  linkCode?: string;
  /** True only the first time this linkCode is detected. */
  openExpanded?: boolean;
}

export const hasWidgetIntroBeenShownForLinkCode = (
  linkCode: string,
): boolean => {
  return localStorage.getItem(getLinkCodeIntroSeenKey(linkCode)) === "true";
};

export const markWidgetIntroPersisted = (linkCode: string): void => {
  localStorage.setItem(getLinkCodeIntroSeenKey(linkCode), "true");
};

export const markWidgetIntroShownForLinkCode = (linkCode: string): void => {
  markWidgetIntroPersisted(linkCode);
  sessionStorage.setItem(LINK_CODE_WIDGET_INTRO_SHOWN_KEY, "true");
};

const clearWidgetIntroForLinkCode = (linkCode: string): void => {
  localStorage.removeItem(getLinkCodeIntroSeenKey(linkCode));
};

export const captureLinkCodeFromUrl = (search: string): LinkCodeCaptureResult => {
  const linkCode = new URLSearchParams(search).get("linkCode")?.trim();
  if (!linkCode) return { captured: false };

  const previousCode = getStoredLinkCodeData()?.linkCode;
  const isNewLinkCode = previousCode !== linkCode;
  const openExpanded = !hasWidgetIntroBeenShownForLinkCode(linkCode);

  if (isNewLinkCode) {
    sessionStorage.removeItem(LINK_CODE_WIDGET_POS_KEY);
  }

  sessionStorage.setItem(
    LINK_CODE_STORAGE_KEY,
    JSON.stringify({ linkCode } satisfies StoredLinkCode),
  );

  if (openExpanded) {
    markWidgetIntroPersisted(linkCode);
    sessionStorage.removeItem(LINK_CODE_WIDGET_INTRO_SHOWN_KEY);
  } else {
    markWidgetIntroShownForLinkCode(linkCode);
  }

  dispatchLinkCodeEvent(LINK_CODE_UPDATED_EVENT);
  return { captured: true, linkCode, openExpanded };
};

export const clearLinkCode = (): void => {
  const linkCode = getStoredLinkCodeData()?.linkCode;
  if (linkCode) {
    clearWidgetIntroForLinkCode(linkCode);
  }

  sessionStorage.removeItem(LINK_CODE_STORAGE_KEY);
  sessionStorage.removeItem(LINK_CODE_WIDGET_POS_KEY);
  sessionStorage.removeItem(LINK_CODE_WIDGET_INTRO_SHOWN_KEY);
  dispatchLinkCodeEvent(LINK_CODE_CLEARED_EVENT);
};

export const hasLinkCodeWidgetIntroShown = (): boolean => {
  return sessionStorage.getItem(LINK_CODE_WIDGET_INTRO_SHOWN_KEY) === "true";
};

export const shouldMinimizeLinkCodeWidget = (): boolean => {
  const linkCode = getStoredLinkCodeData()?.linkCode;
  if (!linkCode) return false;

  if (hasLinkCodeWidgetIntroShown()) return true;

  return hasWidgetIntroBeenShownForLinkCode(linkCode);
};

export const markLinkCodeWidgetIntroShown = (): void => {
  const linkCode = getStoredLinkCodeData()?.linkCode;
  if (!linkCode) return;

  markWidgetIntroShownForLinkCode(linkCode);
};

export const getStoredLinkCodeData = (): StoredLinkCode | null => {
  try {
    const raw = sessionStorage.getItem(LINK_CODE_STORAGE_KEY);
    if (!raw) return null;

    const stored: StoredLinkCode = JSON.parse(raw);
    if (!stored?.linkCode) {
      clearLinkCode();
      return null;
    }

    return stored;
  } catch {
    clearLinkCode();
    return null;
  }
};

export const getStoredWidgetPosition = (): LinkCodeWidgetPosition | null => {
  try {
    const raw = sessionStorage.getItem(LINK_CODE_WIDGET_POS_KEY);
    if (!raw) return null;

    const position: LinkCodeWidgetPosition = JSON.parse(raw);
    if (
      typeof position?.x !== "number" ||
      typeof position?.y !== "number" ||
      Number.isNaN(position.x) ||
      Number.isNaN(position.y)
    ) {
      sessionStorage.removeItem(LINK_CODE_WIDGET_POS_KEY);
      return null;
    }

    return position;
  } catch {
    sessionStorage.removeItem(LINK_CODE_WIDGET_POS_KEY);
    return null;
  }
};

export const saveWidgetPosition = (position: LinkCodeWidgetPosition): void => {
  sessionStorage.setItem(LINK_CODE_WIDGET_POS_KEY, JSON.stringify(position));
};

export const clampWidgetPosition = (
  position: LinkCodeWidgetPosition,
  size: LinkCodeWidgetSize,
): LinkCodeWidgetPosition => {
  const { width, height, offsetTop, offsetLeft } = getViewportMetrics();

  const minX = offsetLeft + VIEWPORT_EDGE_MARGIN;
  const minY = offsetTop + VIEWPORT_EDGE_MARGIN;
  const maxX = offsetLeft + width - size.width - VIEWPORT_EDGE_MARGIN;
  const maxY =
    offsetTop + height - size.height - VIEWPORT_BOTTOM_MARGIN;

  return {
    x: Math.min(Math.max(minX, position.x), Math.max(minX, maxX)),
    y: Math.min(Math.max(minY, position.y), Math.max(minY, maxY)),
  };
};

export const getDefaultWidgetPosition = (
  size: LinkCodeWidgetSize,
): LinkCodeWidgetPosition => {
  const { width, height, offsetTop, offsetLeft } = getViewportMetrics();

  return clampWidgetPosition(
    {
      x: offsetLeft + width - size.width - VIEWPORT_EDGE_MARGIN,
      y: offsetTop + height - size.height - VIEWPORT_BOTTOM_MARGIN,
    },
    size,
  );
};

export const getMinimizedWidgetPosition = (
  size = LINK_CODE_WIDGET_MINIMIZED_SIZE,
): LinkCodeWidgetPosition => {
  return getDefaultWidgetPosition({ width: size, height: size });
};

export const stripLinkCodeFromUrl = (): void => {
  const params = new URLSearchParams(window.location.search);
  if (!params.has("linkCode")) return;

  params.delete("linkCode");

  const query = params.toString();
  const newUrl = `${window.location.pathname}${query ? `?${query}` : ""}${window.location.hash}`;
  window.history.replaceState({}, "", newUrl);
};

export const getLinkCodeFromUrl = (): string | null => {
  const linkCode = new URLSearchParams(window.location.search)
    .get("linkCode")
    ?.trim();
  return linkCode || null;
};

export const getStoredLinkCode = (): string | null => {
  return getStoredLinkCodeData()?.linkCode || null;
};

/** Prefer current URL (last click), then persisted session storage. */
export const getLinkCodeForRequest = (): string | null => {
  return getLinkCodeFromUrl() || getStoredLinkCode();
};

export const hasActiveLinkCode = (): boolean => {
  return Boolean(getStoredLinkCodeData()?.linkCode);
};
